import React, { useState } from 'react';
import { Search, ShieldCheck, AlertTriangle, AlertOctagon, Loader2 } from 'lucide-react';

/**
 * TrademarkSearchPage — knock-out trademark search via USPTO TSDR.
 *
 * Calls POST /api/v1/brand-ip/trademark/search. Returns BLOCK / WARN / CLEAR
 * verdict + list of conflicting marks. This is the first user-facing slice
 * of the Brand IP department; full filing flow (TEAS submission + payment +
 * DocuSign) is a separate workstream.
 */
export default function TrademarkSearchPage() {
  const [mark, setMark] = useState('');
  const [niceClass, setNiceClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!mark.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const token = sessionStorage.getItem('session_token');
      const res = await fetch(
        (process.env.REACT_APP_BACKEND_URL || '') + '/api/v1/brand-ip/trademark/search',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            mark_text: mark.trim(),
            nice_class: niceClass ? parseInt(niceClass, 10) : undefined,
            limit: 25,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      setResult(await res.json());
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const verdictMeta = {
    BLOCK: { color: '#FF6B8A', icon: AlertOctagon, label: 'Blocked', desc: 'A direct conflict exists in the same class. Do not file as-is.' },
    WARN: { color: '#FFD166', icon: AlertTriangle, label: 'Caution', desc: 'A similar mark exists in a different class. Attorney review recommended.' },
    CLEAR: { color: '#3EF1C7', icon: ShieldCheck, label: 'Clear to file', desc: 'No direct conflicts found. Proceed to staging the filing for human review.' },
    PENDING_API_KEY: { color: '#94A3B8', icon: AlertTriangle, label: 'Search not yet wired', desc: 'USPTO Open Data Portal API key required to run real-time knockout search.' },
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-3">Trademark Knock-out Search</h1>
        <p className="text-lg text-[#94A3B8] mb-8">
          Real-time USPTO TSDR search before you file. The <code className="text-[#3EF1C7]">brand_search</code> agent
          runs the same check on the backend before any filing advances.
        </p>

        <form onSubmit={handleSearch} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="text-sm text-[#94A3B8] mb-2 block">Wordmark</label>
              <input
                type="text"
                value={mark}
                onChange={(e) => setMark(e.target.value)}
                placeholder="e.g. COMMERCE INDEX"
                className="w-full px-4 py-3 bg-[#0A0F1C] border border-[#22304A] rounded-lg text-white placeholder-[#475569] focus:border-[#3EF1C7] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-2 block">Nice class (optional)</label>
              <input
                type="number"
                min="1"
                max="45"
                value={niceClass}
                onChange={(e) => setNiceClass(e.target.value)}
                placeholder="e.g. 35"
                className="w-full px-4 py-3 bg-[#0A0F1C] border border-[#22304A] rounded-lg text-white placeholder-[#475569] focus:border-[#3EF1C7] focus:outline-none"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || !mark.trim()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold hover:bg-[#3EF1C7]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            {loading ? 'Searching USPTO TSDR…' : 'Run knock-out search'}
          </button>
        </form>

        {error && (
          <div className="bg-[#0D1628] border border-[#FF6B8A]/40 rounded-xl p-4 mb-6">
            <div className="text-[#FF6B8A] text-sm font-medium">Search failed: {error}</div>
          </div>
        )}

        {result && (
          <div>
            {/* Verdict */}
            {(() => {
              const v = verdictMeta[result.overall_verdict] || verdictMeta.CLEAR;
              const Icon = v.icon;
              return (
                <div
                  className="bg-[#0D1628] border rounded-xl p-6 mb-6"
                  style={{ borderColor: `${v.color}40` }}
                >
                  <div className="flex items-start gap-4">
                    <Icon className="w-8 h-8 flex-shrink-0 mt-1" style={{ color: v.color }} />
                    <div>
                      <h2 className="text-2xl font-bold mb-1" style={{ color: v.color }}>{v.label}</h2>
                      <p className="text-sm text-[#94A3B8]">{v.desc}</p>
                      {result.notice && (
                        <p className="text-xs text-[#FFD166] mt-2">{result.notice}</p>
                      )}
                      {result.overall_verdict !== 'PENDING_API_KEY' && (
                        <p className="text-xs text-[#64748B] mt-2">
                          Searched {result.candidates_searched} marks on USPTO for "{result.mark_text}"
                          {result.nice_class ? ` in class ${result.nice_class}` : ''}.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Hits table */}
            {result.hits.length > 0 ? (
              <div className="bg-[#0D1628] border border-[#22304A] rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-[#22304A]">
                  <h3 className="font-semibold">Conflicting & similar marks ({result.hits.length})</h3>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0F1C] text-xs text-[#64748B] uppercase">
                    <tr>
                      <th className="text-left px-6 py-3">Serial #</th>
                      <th className="text-left px-6 py-3">Mark</th>
                      <th className="text-left px-6 py-3">Class</th>
                      <th className="text-left px-6 py-3">Owner</th>
                      <th className="text-left px-6 py-3">Filed</th>
                      <th className="text-left px-6 py-3">Verdict</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#22304A]">
                    {result.hits.map((h) => (
                      <tr key={h.serial_number || h.mark_text}>
                        <td className="px-6 py-3 font-mono text-xs text-[#94A3B8]">{h.serial_number || '—'}</td>
                        <td className="px-6 py-3">{h.mark_text || '—'}</td>
                        <td className="px-6 py-3 text-xs text-[#94A3B8]">
                          {(h.nice_classes || []).join(', ') || '—'}
                        </td>
                        <td className="px-6 py-3 text-xs text-[#94A3B8]">{h.owner || '—'}</td>
                        <td className="px-6 py-3 text-xs text-[#94A3B8]">{h.filing_date || '—'}</td>
                        <td className="px-6 py-3">
                          <span
                            className="text-xs px-2 py-1 rounded"
                            style={{
                              color:
                                h.verdict === 'DIRECT_CONFLICT'
                                  ? '#FF6B8A'
                                  : h.verdict === 'SOFT_MATCH'
                                    ? '#FFD166'
                                    : '#94A3B8',
                              backgroundColor:
                                h.verdict === 'DIRECT_CONFLICT'
                                  ? '#FF6B8A15'
                                  : h.verdict === 'SOFT_MATCH'
                                    ? '#FFD16615'
                                    : '#22304A',
                            }}
                          >
                            {h.verdict.replace('_', ' ')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-8 text-center text-[#94A3B8]">
                No similar marks found on USPTO TSDR.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
